import type { WazuhDateRangeOption } from 'src/mindtime/hooks/useWazuhScope';
import type {
  WazuhEndpointHealthPillar,
  WazuhEndpointItem,
  WazuhEndpointSecurityOverviewPeriod,
} from 'src/types/mindtime/wazuhEndpoints';
import type { AgentStatus, EndpointRisk, EndpointRow, EndpointStatus } from './types';

export const toEndpointSecurityOverviewPeriod = (
  dateRange: WazuhDateRangeOption
): WazuhEndpointSecurityOverviewPeriod =>
  dateRange === 'Last 30 days' ? '30d' : '7d';

export const ENDPOINT_SECURITY_OVERVIEW_LABELS = [
  'Threats Detected',
  'SCA Checks Failed',
  'File Integrity Changes',
  'Open Vulnerabilities',
] as const;

type SecurityOverviewCounts = {
  threatsDetected?: number;
  scaChecksFailed?: number;
  fileIntegrityChanges?: number;
  openVulnerabilities?: number;
};

/** Map security-overview API fields to SecuritySeverityCompareBarChart count order. */
export const mapEndpointSecurityOverviewCounts = (data?: SecurityOverviewCounts): number[] => [
  data?.threatsDetected ?? 0,
  data?.scaChecksFailed ?? 0,
  data?.fileIntegrityChanges ?? 0,
  data?.openVulnerabilities ?? 0,
];

const normalizeKey = (value: string | undefined): string =>
  (value ?? '').toString().trim().toLowerCase();

export const toEndpointRisk = (value: string | undefined): EndpointRisk => {
  const key = normalizeKey(value);
  if (key === 'critical' || key === 'high' || key === 'medium' || key === 'low') return key;
  return 'low';
};

const toHealthTone = (score: number): 'success' | 'warning' | 'danger' => {
  if (score >= 85) return 'success';
  if (score >= 60) return 'warning';
  return 'danger';
};

/** Map /health pillars dynamically (new keys do not require FE schema changes). */
export const mapEndpointHealthPillars = (pillars?: WazuhEndpointHealthPillar[]) => {
  if (!pillars?.length) return [];
  return pillars.map((pillar, index) => {
    const score = Math.min(100, Math.max(0, Math.round(pillar.score ?? 0)));
    return {
      id: pillar.key?.trim() || `pillar-${index}`,
      label: pillar.label?.trim() || pillar.key?.trim() || `Pillar ${index + 1}`,
      percent: score,
      tone: toHealthTone(score),
    };
  });
};

const toEndpointStatus = (value: string | undefined): EndpointStatus => {
  const key = normalizeKey(value).replace(/-/g, '_');
  if (key === 'never_connected') return 'never_connected';
  if (key === 'disconnected' || key === 'inactive') return 'disconnected';
  if (key === 'offline') return 'offline';
  return 'active';
};

const toAgentStatus = (value: string | undefined): AgentStatus => {
  const key = normalizeKey(value);
  return key === 'offline' || key === 'disconnected' ? 'offline' : 'online';
};

export const mapWazuhEndpointItem = (item: WazuhEndpointItem): EndpointRow => {
  const id = String(item.systemWazuhAgentId ?? '');
  const endpoint = String(item.endpoint ?? item.agentName ?? item.name ?? id);
  const statusSource = item.status ?? item.agentStatus;
  const riskSource = item.riskLevel ?? item.risk;
  const lastSeenSource = item.lastSeen ?? item.lastKeepAlive;

  return {
    id,
    endpoint,
    location: item.location ?? '—',
    status: toEndpointStatus(statusSource),
    risk: toEndpointRisk(riskSource),
    os: item.os ?? item.system ?? item.osPlatform ?? '—',
    agent: toAgentStatus(item.agentStatus ?? item.status),
    vulnerabilities: item.vulnerabilities ?? item.vulnerabilityCount ?? 0,
    alerts: item.alerts ?? item.alertCount ?? 0,
    criticalAlerts: item.criticalAlerts ?? 0,
    criticalVulnerabilities: item.criticalVulnerabilities ?? 0,
    openIssues: item.openIssues ?? item.openIssueCount ?? 0,
    overallComplianceScore: item.overallComplianceScore ?? 0,
    lastSeen: lastSeenSource || '—',
  };
};
